import React, { useCallback } from 'react';
import { useDroppable } from '@dnd-kit/core';
import ExpressionBlock from './ExpressionBlock';
import { functionRegistry } from '../../utils/functionRegistry';
import type { ExpressionGraph, Instrument, Indicator } from '../../state/types';

function DropZone({
  children,
  isPromoteTarget,
}: {
  children?: React.ReactNode;
  isPromoteTarget: boolean;
}) {
  const { setNodeRef, isOver } = useDroppable({ id: 'expression-canvas' });

  return (
    <div
      id="expression-canvas"
      data-id="expression-canvas"
      data-type="expression-canvas"
      ref={setNodeRef}
      className={`min-h-[200px] p-4 border-2 border-dashed rounded-sm w-full min-w-0
        ${isPromoteTarget ? 'bg-newsprint-dark border-rule' :
          isOver ? 'bg-table-win border-ink' : 'bg-newsprint-dark border-rule'}
      `}
    >
      {children ?? 'Drop here to build expression...'}
    </div>
  );
}

type Props = {
  expressionGraph: ExpressionGraph;
  /** Horizontal budget (px) for expression blocks before switching to vertical stacking. */
  layoutBudgetPx?: number;
  rootRef: React.RefObject<HTMLDivElement>;
  isPromoteTarget: boolean;
  onRequestEditSeries?: (args: {
    expressionId: string;
    slotIndex: number;
    operandId: string;
  }) => void;
  hoveredPath: string[];
  isDragging?: boolean;
  instruments: Instrument[];
  indicators: Indicator[];
  onInsertLiteral: (
    parentId: string,
    index: number,
    value: number | string,
    isString?: boolean
  ) => void;
  onUpdateEpsilon: (expressionId: string, epsilon: number) => void;
  onRemoveOperand: (parentId: string, index: number) => void;
  onClearExpression: () => void;
};

const ExpressionCanvas: React.FC<Props> = ({
  expressionGraph,
  layoutBudgetPx,
  rootRef,
  isPromoteTarget,
  onRequestEditSeries,
  hoveredPath,
  isDragging,
  instruments,
  indicators,
  onInsertLiteral,
  onUpdateEpsilon,
  onRemoveOperand,
  onClearExpression,
}) => {
  const expressions = expressionGraph.expressions;

  const renderNode = useCallback((
    expressionId: string,
    isDragging?: boolean,
    hoveredPath: string[] = []
  ): React.ReactNode => {
    const node = expressions[expressionId];
    if (!node) return null;

    if (node.type === 'ohlc_series') {
      const instrument = instruments.find((inst) => inst.id === node.seriesId);
      const symbol = instrument?.symbol ?? 'Unknown';
      return (
        <div key={expressionId} className="px-2 py-1 text-sm border border-rule rounded-sm bg-newsprint text-ink">
          {`${symbol}.${node.field} (${node.offset}d)`}
        </div>
      );
    }

    if (node.type === 'indicator_series') {
      const indicator = indicators.find((ind) => ind.id === node.seriesId);
      const instrument = instruments.find((inst) => inst.id === indicator?.instrumentId);
      const symbol = instrument?.symbol ?? 'Unknown';
      const label = indicator ? (indicator.name || `${symbol} ${indicator.type}`) : 'Unknown Indicator';
      return (
        <div key={expressionId} className="px-2 py-1 text-sm border border-rule rounded-sm bg-newsprint text-ink">
          {`${label} (${node.offset}d)`}
        </div>
      );
    }

    if (node.type === 'literal') {
      return (
        <div key={expressionId} className="px-2 py-1 text-sm border border-rule rounded-sm bg-table-win text-ink">
          {node.value}
        </div>
      );
    }

    if (node.type !== 'function') return null;

    const def = functionRegistry.get(node.functionId);
    const isSeriesAwareOnly = def && def.parameters.length > 0 && def.parameters.every(
      (p: { type: string }) => p.type === 'seriesAware' || p.type === 'seriesAwareWindow'
    );
    const displayOperandIds = isSeriesAwareOnly && def
      ? (node.operandIds || []).slice(0, def.parameters.length)
      : (node.operandIds || []);

    const childElements = displayOperandIds.map((childId: string | null) => {
      if (!childId) return null;
      const childNode = expressions[childId];
      if (!childNode || childNode.type === 'literal') return null;
      return renderNode(childId, isDragging, hoveredPath);
    });

    return (
      <ExpressionBlock
        key={expressionId}
        id={expressionId}
        functionId={node.functionId}
        operandIds={displayOperandIds}
        expressions={expressions}
        layoutBudgetPx={layoutBudgetPx}
        childElements={childElements}
        onRemoveOperand={(_parentId: string, index: number) => onRemoveOperand(expressionId, index)}
        onInsertLiteral={onInsertLiteral}
        onUpdateEpsilon={onUpdateEpsilon}
        onRequestEditSeries={onRequestEditSeries}
        rootRef={rootRef}
        isHoveredDuringDrag={hoveredPath.includes(expressionId)}
        isDragging={isDragging}
      />
    );
  }, [
    expressions,
    instruments,
    indicators,
    layoutBudgetPx,
    onRequestEditSeries,
    onRemoveOperand,
    onInsertLiteral,
    onUpdateEpsilon,
  ]);

  return (
    <div id="expression-canvas" data-testid="expression-canvas" className="flex flex-col gap-2 w-full min-w-0">
      <DropZone isPromoteTarget={isPromoteTarget}>
        {expressionGraph.rootId ? (
          <div className="flex flex-col items-stretch gap-2 min-w-0 sm:flex-row sm:items-start">
            <div ref={rootRef} className="min-w-0 flex-1">
              {renderNode(expressionGraph.rootId, isDragging, hoveredPath)}
            </div>

            {/* Same look/feel as other delete buttons */}
            <button
              type="button"
              className="shrink-0 self-end sm:self-start ml-0 sm:ml-2 text-error text-xs hover:underline"
              aria-label="Clear root expression"
              onClick={(e) => {
                e.stopPropagation();
                onClearExpression();
              }}
              onPointerDown={(e) => e.stopPropagation()}
            >
              ×
            </button>
          </div>
        ) : (
          <span className="text-ink/70 italic">Drop a function to start</span>
        )}
      </DropZone>

      {expressionGraph.rootId && (
        <button
          className="self-start text-sm text-error underline mt-1"
          onClick={onClearExpression}
        >
          Clear Expression
        </button>
      )}
    </div>
  );
};

export default ExpressionCanvas;